import { ClearOutlined } from '@mui/icons-material'
import {
  IconButton,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { useCallback, useEffect, useState } from 'react'
import { PaymentTermsDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useShowException } from 'utils/useShowException'
import { PaymentTermsController } from './PaymentTermsController'

type PaymentTermsSelectorProps = TextFieldProps & {
  selectedPaymentTermCode: string | null
  onPaymentTermSelected?: (deliveryTermCode: string) => void
  onCleaned: () => void
  labelClassName?: string
}

export const PaymentTermsSelector = (props: PaymentTermsSelectorProps) => {
  const { controller } = useAppController(() => new PaymentTermsController())

  const {
    selectedPaymentTermCode,
    onPaymentTermSelected,
    onCleaned,
    label,
    ...textFieldProps
  } = props

  const ShowException = useShowException('payment terms')

  const [paymentTerms, setPaymentTerms] = useState<PaymentTermsDto[]>([])

  const getPaymentTerms = useCallback(async () => {
    try {
      const resp = await controller.getSelectablePaymentTerms()
      setPaymentTerms(resp.records)
    } catch (err) {
      ShowException(err)
    }
  }, [ShowException, controller])

  useEffect(() => {
    getPaymentTerms()
  }, [getPaymentTerms])

  if (!paymentTerms.length) return null

  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          className={props.labelClassName}
          sx={{
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        select
        onChange={(e) => onPaymentTermSelected?.(e.target.value)}
        value={selectedPaymentTermCode || ''}
        fullWidth
        placeholder={props.placeholder}
        slotProps={{
          input: {
            sx: {
              color: selectedPaymentTermCode ? 'textPrimary' : 'GrayText',
            },
          },
          select: {
            displayEmpty: true,
          },
        }}
        {...textFieldProps}
      >
        <MenuItem value="" disabled>
          {props.placeholder}
        </MenuItem>
        {paymentTerms.map((term) => (
          <MenuItem key={term.code} value={term.code}>
            {`${term.code} - ${term.description}`}
          </MenuItem>
        ))}
      </TextField>
      {props.selectedPaymentTermCode && (
        <IconButton
          style={{
            position: 'absolute',
            right: '2rem',
            bottom: '1px',
          }}
          size="small"
          onClick={onCleaned}
        >
          <ClearOutlined fontSize="small" />
        </IconButton>
      )}
    </div>
  )
}
